import React, { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        setIsAuthenticated(true);

        // Fetch user profile from Firestore
        try {
          const profileDoc = await getDoc(doc(db, "users", firebaseUser.uid));
          if (profileDoc.exists()) {
            setUserProfile(profileDoc.data());
          }
        } catch (err) {
          console.error("Failed to fetch user profile:", err);
        }
      } else {
        setUser(null);
        setUserProfile(null);
        setIsAuthenticated(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);

      // Update last login timestamp
      await setDoc(
        doc(db, "users", result.user.uid),
        { lastLogin: serverTimestamp() },
        { merge: true }
      );

      return { success: true };
    } catch (error) {
      let message = "Login failed. Please check your credentials.";
      if (error.code === "auth/user-not-found" || error.code === "auth/wrong-password") {
        message = "Invalid email or password.";
      } else if (error.code === "auth/invalid-credential") {
        message = "Invalid email or password.";
      } else if (error.code === "auth/too-many-requests") {
        message = "Too many attempts. Please try again later.";
      }
      return { success: false, error: message };
    }
  };

  const signup = async (userData) => {
    try {
      const result = await createUserWithEmailAndPassword(
        auth,
        userData.email,
        userData.password
      );

      // Set display name
      const displayName = `${userData.firstName} ${userData.lastName}`;
      await updateProfile(result.user, { displayName });

      // Create user profile in Firestore
      await setDoc(doc(db, "users", result.user.uid), {
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        displayName,
        company: userData.company || "",
        phone: userData.phone || "",
        role: "user",
        isActive: true,
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
      });

      return { success: true, data: result.user };
    } catch (error) {
      let message = "Registration failed. Please try again.";
      if (error.code === "auth/email-already-in-use") {
        message = "An account with this email already exists.";
      } else if (error.code === "auth/weak-password") {
        message = "Password must be at least 6 characters.";
      } else if (error.code === "auth/invalid-email") {
        message = "Invalid email address.";
      }
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setUserProfile(null);
    setIsAuthenticated(false);
  };

  const updateUser = async (data) => {
    try {
      if (!user) throw new Error("Not authenticated");

      // Update Firebase Auth display name if changed
      if (data.firstName || data.lastName) {
        const displayName = `${data.firstName || userProfile?.firstName || ""} ${data.lastName || userProfile?.lastName || ""}`.trim();
        await updateProfile(user, { displayName });
      }

      // Update Firestore profile
      await setDoc(doc(db, "users", user.uid), data, { merge: true });

      // Refresh local profile
      const updatedDoc = await getDoc(doc(db, "users", user.uid));
      setUserProfile(updatedDoc.data());

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    userProfile,
    isAuthenticated,
    loading,
    login,
    signup,
    logout,
    updateUser,

    // Helper methods
    getUserDisplayName: () => {
      return userProfile?.displayName || user?.displayName || user?.email || "User";
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
